import { Button } from "@/components/ui/button";
import {
  MetaLoginCancelled,
  openMetaLogin,
} from "@/features/connectors/meta-popup";
import {
  MetaAPI,
  useConnectWhatsApp,
  useMetaSettings,
  type WhatsAppNumber,
} from "@/features/connectors";
import { useTranslation } from "@/features/utils/i18n";
import { CircleNotch, WhatsappLogo } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";
import { OtpHelper } from "./otp-helper";

interface Props {
  clientId: string;
  agentId: string;
  onConnected: () => void;
}

export const WhatsAppPanel = ({ clientId, agentId, onConnected }: Props) => {
  const { t } = useTranslation("connectors");
  const { data: settings } = useMetaSettings(clientId);
  const connect = useConnectWhatsApp();
  const [busy, setBusy] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  // When the dialog did not say which number was chosen and the login reaches several:
  // the person picks, as for a Page.
  const [handle, setHandle] = useState("");
  const [numbers, setNumbers] = useState<WhatsAppNumber[] | null>(null);
  const [selected, setSelected] = useState("");

  const finish = async (loginHandle: string, chosen?: WhatsAppNumber) => {
    await connect.mutateAsync({
      clientId,
      data: {
        handle: loginHandle,
        ...(chosen
          ? { waba_id: chosen.waba_id, phone_number_id: chosen.phone_number_id }
          : {}),
        agent_id: agentId || undefined,
      },
    });
    toast.success(t("meta.connected"));
    onConnected();
  };

  const start = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      // The popup opens on the click; the dialog's URL follows.
      const login = await openMetaLogin(
        MetaAPI.authorizeUrl(clientId, "whatsapp"),
      );
      if (login.whatsapp) {
        await connect.mutateAsync({
          clientId,
          data: {
            handle: login.handle,
            ...login.whatsapp,
            agent_id: agentId || undefined,
          },
        });
        toast.success(t("meta.connected"));
        onConnected();
        return;
      }

      // Embedded Signup's own message never reached us: ask which numbers this login reaches.
      const { numbers: reachable } = await MetaAPI.getWhatsAppNumbers(
        clientId,
        login.handle,
      );
      if (reachable.length === 1)
        return await finish(login.handle, reachable[0]);
      if (!reachable.length) {
        toast.error(t("meta.whatsapp.noNumber"));
        return;
      }
      setHandle(login.handle);
      setNumbers(reachable);
      setSelected("");
    } catch (error) {
      if (error instanceof MetaLoginCancelled) return;
      toast.error(
        error instanceof Error ? error.message : t("meta.connectError"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("meta.whatsapp.intro")}
      </p>

      {numbers && numbers.length > 1 ? (
        <div className="space-y-3">
          <p className="text-sm">{t("meta.whatsapp.chooseNumber")}</p>
          <div className="space-y-2" role="radiogroup">
            {numbers.map((number) => (
              <label
                key={number.phone_number_id}
                className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 has-[:checked]:border-primary"
              >
                <input
                  type="radio"
                  name="meta-whatsapp-number"
                  value={number.phone_number_id}
                  checked={selected === number.phone_number_id}
                  onChange={() => setSelected(number.phone_number_id)}
                />
                <span className="text-sm">
                  {number.display_phone_number || number.phone_number_id}
                  {(number.verified_name || number.waba_name) && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {number.verified_name || number.waba_name}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
          <Button
            type="button"
            disabled={!selected || connect.isPending}
            onClick={async () => {
              try {
                await finish(
                  handle,
                  numbers.find((n) => n.phone_number_id === selected),
                );
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : t("meta.connectError"),
                );
              }
            }}
          >
            {connect.isPending && (
              <CircleNotch className="h-4 w-4 animate-spin" />
            )}
            {t("meta.connect")}
          </Button>
        </div>
      ) : (
        <Button type="button" onClick={start} disabled={busy || !settings}>
          {busy ? (
            <CircleNotch className="h-4 w-4 animate-spin" />
          ) : (
            <WhatsappLogo className="h-4 w-4" weight="fill" />
          )}
          {t("meta.continueWithFacebook")}
        </Button>
      )}

      <div className="space-y-2">
        <button
          type="button"
          className="text-sm underline underline-offset-4"
          onClick={() => setShowOtp((v) => !v)}
        >
          {t("meta.otp.toggle")}
        </button>
        {showOtp && <OtpHelper clientId={clientId} />}
      </div>
    </div>
  );
};
